import React from 'react';
import { Box, Button, Link, Paper, Typography } from '@mui/material';
import { offerButtonCostLabel, offerCostLabel } from './chatEnrichmentOfferModel';
import { isFreeExportAction } from './chatArtifactExportModel';

function firstPresent(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '');
}

function referenceTitle(reference) {
  const rank = reference?.rank ? `#${reference.rank} ` : '';
  return `${rank}${firstPresent(reference?.company_name, reference?.company_id, 'Referenced company')}`;
}

function referenceEvidence(reference) {
  if (typeof reference?.reason === 'string' && reference.reason.trim()) return reference.reason;
  return firstPresent(reference?.snippet, reference?.value, reference?.text, reference?.url);
}

function disclosureText(reference) {
  return firstPresent(reference?.disclosure_message, reference?.disclosure_label, reference?.disclosure);
}

function actionReason(action) {
  return [
    action?.disabled_reason,
    action?.cost_label || offerCostLabel(action),
    action?.scope_label,
    action?.scope_count !== undefined && action?.scope_count !== null
      ? `${action.scope_count} ${action.scope_count === 1 ? 'company' : 'companies'}`
      : null,
    action?.query ? `Researching: ${action.query}` : null,
  ].filter(Boolean).join(' · ');
}

export default function ChatArtifactAnswerEvidence({
  answer = null,
  actions = [],
  actionInFlightKey = null,
  onRunAction,
}) {
  const references = Array.isArray(answer?.evidence_references)
    ? answer.evidence_references
    : [];
  const visibleActions = Array.isArray(actions)
    ? actions.filter((action) => action?.key === 'answer_by_enriching' || action?.proposed || isFreeExportAction(action))
    : [];

  if (references.length === 0 && visibleActions.length === 0) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 1,
        p: 1.25,
        width: 'min(100%, 680px)',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        overflowWrap: 'anywhere',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px',
        bgcolor: 'background.paper',
      }}
    >
      {references.length > 0 && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0 }}>
            Evidence
          </Typography>
          {references.map((reference, index) => {
            const evidence = referenceEvidence(reference);
            const disclosure = disclosureText(reference);
            const key = `${reference?.company_id || 'reference'}-${reference?.field_key || index}`;
            const isUrl = /^https?:\/\//i.test(String(evidence || ''));
            return (
              <Box key={key}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {referenceTitle(reference)}
                </Typography>
                {evidence && (
                  <Typography variant="body2" sx={{ mt: 0.25, wordBreak: 'break-word' }}>
                    {isUrl ? (
                      <Link href={evidence} target="_blank" rel="noreferrer" underline="hover">
                        {evidence}
                      </Link>
                    ) : evidence}
                  </Typography>
                )}
                {disclosure && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    {`Disclosure: ${disclosure}`}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {visibleActions.length > 0 && (
        <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.75} mt={references.length ? 1.25 : 0}>
          {visibleActions.map((action) => {
            const disabled = action.enabled === false || Boolean(actionInFlightKey);
            const reason = actionReason(action);
            return (
              <Box key={action.key}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={disabled}
                  onClick={() => onRunAction?.(action.key)}
                  sx={{ borderRadius: '8px', textTransform: 'none' }}
                >
                  {actionInFlightKey === action.key ? 'Running...' : [
                    firstPresent(action.label, 'Enrich saved evidence'),
                    isFreeExportAction(action) ? null : offerButtonCostLabel(action),
                  ].filter(Boolean).join(' · ')}
                </Button>
                {reason && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    {reason}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Paper>
  );
}
